/**
 * Go-live readiness for one customer (Customers & Onboarding, part B). Counts
 * from the tenant's own records, read by the platform door; the rosters behind
 * them stay in the customer app. A read that did not complete is never shown
 * as zeros.
 */
import type { ConsoleOutcome } from '../platform/client.js';
import type { CustomerReadinessRead, FirstAdminState } from '../platform/tenancy.js';

export type ReadinessOutcome = ConsoleOutcome<CustomerReadinessRead> | null | undefined;

type Tone = 'good' | 'look' | 'urgent' | 'unsure';

const FIRST_ADMIN: Record<FirstAdminState, [Tone, string]> = {
  none: ['unsure', 'no operator invitation on record'],
  pending: ['look', 'invited, not yet accepted'],
  pending_past_expiry: ['urgent', 'invitation expired unanswered'],
  accepted: ['good', 'accepted'],
  revoked: ['look', 'invitation revoked'],
  expired: ['urgent', 'invitation expired'],
};

/** What each milestone means, and what to do when it is not done. Customer-side work. */
export const MILESTONE_TEXT: Record<string, { label: string; blocker: string }> = {
  administrator_active: {
    label: 'An administrator is active',
    blocker: 'The first administrator has not accepted. Resend or replace the invitation below.',
  },
  site_configured: {
    label: 'At least one site',
    blocker: 'The customer adds sites in Admin → Sites.',
  },
  sites_have_coordinates: {
    label: 'Every site has coordinates',
    blocker: 'Some sites have no location, so clock-in location checks cannot run there.',
  },
  program_linked_to_site: {
    label: 'A program is linked to a site',
    blocker: 'The customer creates programs and links them to sites.',
  },
  integration_synced: {
    label: 'The EHR connector has synced',
    blocker: 'No connector has completed a sync. See Integrations.',
  },
  workers_invited: {
    label: 'Workers are invited',
    blocker: 'The customer invites workers in Admin → People.',
  },
  workers_assigned_to_programs: {
    label: 'Workers are assigned to programs',
    blocker: 'No active program has a current worker assignment.',
  },
  schedule_in_place: {
    label: 'Work is scheduled (next 14 days)',
    blocker: 'No shifts or client services are scheduled in the next two weeks.',
  },
  evv_configured: {
    label: 'EVV mappings and attestation policy',
    blocker:
      'EVV needs a service-to-procedure mapping and an attestation policy in effect (configuration only, not aggregator acceptance).',
  },
  rules_enforcing: {
    label: 'At least one rule is enforcing',
    blocker: 'All workflow rules are off or simulating.',
  },
};

/** At least one, or not: the closed reading every card below uses. */
const atLeastOne = (n: number): Tone => (n > 0 ? 'good' : 'look');

function Card({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: string;
  tone: Tone;
  note: string;
}) {
  return (
    <div className="metric">
      <span className="metric-label">{label}</span>
      <span className="pill" data-tone={tone}>
        {value}
      </span>
      <span className="metric-note">{note}</span>
    </div>
  );
}

export function ReadinessSection({ outcome }: { outcome: ReadinessOutcome }) {
  if (!outcome)
    return (
      <section>
        <h3>Go-live readiness</h3>
        <p className="quiet">Reading the customer’s records…</p>
      </section>
    );
  if (outcome.kind !== 'ok')
    return (
      <section>
        <h3>Go-live readiness</h3>
        <div className="banner" role="alert">
          <strong>Readiness did not answer.</strong> {outcome.message}
        </div>
      </section>
    );
  const r = outcome.value.readiness;
  const [adminTone, adminText] =
    r.firstAdmin.activeAdmins > 0
      ? (['good', 'active'] as const)
      : FIRST_ADMIN[r.firstAdmin.invitation];
  return (
    <section>
      <h3>Go-live readiness</h3>
      <div className="metrics">
        <Card
          label="Administrator"
          value={r.firstAdmin.activeAdmins > 0 ? `${r.firstAdmin.activeAdmins} active` : adminText}
          tone={adminTone}
          note={
            r.firstAdmin.invitationExpiresAt && r.firstAdmin.activeAdmins === 0
              ? `invitation expires ${new Date(r.firstAdmin.invitationExpiresAt).toLocaleString()}`
              : 'org_admin accounts in status active'
          }
        />
        <Card
          label="Sites"
          value={`${r.sites.active} active`}
          tone={atLeastOne(r.sites.active)}
          note={`${r.sites.withCoordinates} with coordinates for clock-in checks`}
        />
        <Card
          label="Programs"
          value={`${r.programs.active} active`}
          tone={atLeastOne(r.programs.linkedToSite)}
          note={`${r.programs.linkedToSite} linked to a site · ${r.programs.withWorkers} with workers`}
        />
        <Card
          label="EVV configuration"
          value={`${r.evv.serviceMappingsInEffect} mappings`}
          tone={atLeastOne(r.evv.serviceMappingsInEffect)}
          note={`${r.evv.attestationPoliciesInEffect} attestation policies in effect · configuration present, not aggregator acceptance`}
        />
        <Card
          label="Rules"
          value={`${r.rules.enabled} enforcing`}
          tone={r.rules.suspended > 0 ? 'urgent' : atLeastOne(r.rules.enabled + r.rules.simulating)}
          note={`${r.rules.simulating} simulating · ${r.rules.suspended} suspended · ${r.locationPolicies} location policies`}
        />
        <Card
          label="Scheduling (next 14 days)"
          value={`${r.scheduling.shiftsNext14Days + r.scheduling.servicesNext14Days} scheduled`}
          tone={atLeastOne(r.scheduling.shiftsNext14Days + r.scheduling.servicesNext14Days)}
          note={`${r.scheduling.shiftsNext14Days} shifts · ${r.scheduling.servicesNext14Days} client services · ${r.scheduling.workersWithProfile} worker profiles`}
        />
        <Card
          label="Workforce"
          value={`${r.workforce.workersInvitedOrActive} workers`}
          tone={atLeastOne(r.workforce.workersInvitedOrActive)}
          note={`invited or active · ${r.workforce.supervisorsActive} active supervisors`}
        />
      </div>
      <h4>Implementation milestones</h4>
      <ol className="milestones">
        {r.milestones.map((m) => {
          const text = MILESTONE_TEXT[m.key] ?? { label: m.key, blocker: '' };
          return (
            <li key={m.key} data-done={m.done ? 'true' : 'false'}>
              <span className="pill" data-tone={m.done ? 'good' : 'look'}>
                {m.done ? 'done' : 'open'}
              </span>{' '}
              <strong>{text.label}</strong>
              {m.done ? null : <div className="quiet">{text.blocker}</div>}
            </li>
          );
        })}
      </ol>
      <p className="quiet">
        {r.milestones.filter((m) => m.done).length} of {r.milestones.length} milestones done ·
        measured {new Date(outcome.value.measuredAt).toLocaleString()}. Counts come from the
        customer&apos;s own records; no names or addresses leave the customer.
      </p>
    </section>
  );
}
